<template>
  <hls-player
    :link="actualLink"
    :player-time="playerPosition"
    :volume="volume"
    :paused.sync="pausedProp"
    :muted.sync="mutedProp"
    :mode="mode"
    :poster="poster"
    :time="time"
    @loading="isLoaded = false"
    @loaded="handleLoaded"
    @playing="$emit('playing')"
    @played="$emit('played')"
    @timeChanged="handlePlayerPositionChanged"
    @playerRendered="$emit('playerRendered', $event)"
    @imageEmptied="$emit('imageEmptied')"
    @imageLoaded="$emit('imageLoaded')"
  />
</template>

<script>
import defaultPoster from "@/assets/images/loading_stream.jpg";
import HlsPlayer from "@/components/media/video/HlsPlayer.vue";

// @group Media/Video
// Plays long recorded stream by loading short segments
export default {
  name: "SegmentPlayer",
  components: {
    HlsPlayer
  },
  /** if time to segment edge less than it, then segment will be reloaded */
  reloadThreshold: 10,
  /** seconds to end when player moved to live mode */
  liveTreshold: 30,
  /** loadings fragment duration (in seconds) */
  segmentDuration: 60 * 20,
  /** duration of fragment before player (to rewind) */
  preloadDuration: 60 * 2,
  props: {
    /** set absolute current time timestamp */
    time: {
      type: Number,
      required: true,
      validator(val) {
        return Number.isFinite(val) && val >= 0;
      }
    },
    /** set main stream link */
    link: {
      type: String,
    },
    /** set time of stream end */
    maxTime: {
      type: Number,
      default: new Date() / 1000
    },
    /** set time of stream start */
    minTime: {
      type: Number,
      default: 0
    },
    /** set sound volume */
    volume: {
      type: Number,
      default: 1.0,
      validator(vol) {
        return vol >= 0 && vol <= 1;
      }
    },
    /** mute/unmute video */
    muted: {
      type: Boolean,
      default: false
    },
    /** playback mode */
    mode: {
      type: String,
      default: "normal",
      validator(mode) {
        const availableModes = ["rewind", "normal", "fast"];
        return availableModes.includes(mode);
      }
    },
    /** default poster */
    poster: {
      type: String,
      default: defaultPoster
    },
    /** pause/play video */
    paused: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      /** loaded fragment absolute-time start */
      segmentStart: 0,
      /** actual duration of loaded segment */
      actualSegmentDuration: 0,
      /** syncronized with current relative player time */
      playerPosition: 0,
      /** is player in live mode */
      onLive: false,
      /** is player loading */
      isLoaded: true
    };
  },
  computed: {
    // @vuese
    // Settable property to use with .sync
    mutedProp: {
      get() {
        return this.muted;
      },
      set(val) {
        this.$emit("update:muted", val);
      }
    },
    // @vuese
    // Settable property to use with .sync
    pausedProp: {
      get() {
        return this.paused;
      },
      set(val) {
        this.$emit("update:paused", val);
      }
    },
    // @vuese
    // Actual link to play
    actualLink() {
      return this.onLive ? this.link : this.timedLink;
    },
    // @vuese
    // Preferred segment end (to load)
    segmentEnd() {
      return this.segmentStart + this.$options.segmentDuration;
    },
    // @vuese
    // Actual segment end (after loaded)
    actualSegmentEnd() {
      return this.segmentStart + this.actualSegmentDuration;
    },
    // @vuese
    // Stream link with segment timestamps
    timedLink() {
      const start = Math.round(this.segmentStart * 1000);
      const end = Math.round(this.segmentEnd * 1000);
      const timedLink = `${this.link}&DVR&dvrplayliststart=${start}&dvrplaylistend=${end}`;
      return timedLink;
    },
    // @vuese
    // Is start, end and time parameters have initialized and correct
    isTimeValid() {
      return (
        this.minTime <= this.time && this.time <= this.maxTime && this.link
      );
    },
    // @vuese
    // Is it needed to reload current segmwnt or not
    toReloadSegment() {
      return this.onLiveEdge || (this.onSegmentEdge && !this.onStreamStartEdge);
    },
    // @vuese
    // Is time near to stream end
    onLiveEdge() {
      return this.time + this.$options.liveTreshold > this.maxTime;
    },
    // @vuese
    // Is time near to segment's start/end
    onSegmentEdge() {
      return this.onSegmentStartEdge || this.onSegmentEndEdge;
    },
    // @vuese
    // Is time near to segment's start
    onSegmentStartEdge() {
      return this.time < this.segmentStart + this.$options.reloadThreshold;
    },
    // @vuese
    // Is time near to segment's end
    onSegmentEndEdge() {
      return this.time > this.actualSegmentEnd - this.$options.reloadThreshold;
    },
    // @vuese
    // Is time too near to stream start
    onStreamStartEdge() {
      return this.time < this.minTime + this.$options.reloadThreshold;
    }
  },
  watch: {
    time() {
      /** when time is changed on player loading, then we need to reload it again */
      if (!this.isLoaded) {
        if (this.onLiveEdge) {
          this.startLoadLive();
        } else {
          this.startLoadSegment(this.time);
        }
      }

      /** time changed */
      if (this.onLive) {
        if (!this.onLiveEdge) {
          /** player strat to reload when it moved from live time */
          this.isLoaded = false;
        }
      } else {
        if (this.toReloadSegment) {
          /** player start to reload when it's on edge of loaded segment */
          this.isLoaded = false;
        } else {
          /** player just move when there is no need to reload */
          this.movePlayer(this.time);
        }
      }
    },
    isLoaded() {
      if (this.isLoaded) {
        if (!this.onLive) {
          this.movePlayer(this.time);
        }
      } else {
        if (this.onLiveEdge) {
          this.startLoadLive();
        } else {
          this.startLoadSegment(this.time);
        }
      }
    },
    maxTime() {
      if (this.onLive && !this.onLiveEdge) {
        this.isLoaded = false;
      }
    },
    // @vuese
    // Notify parent of changing live mode
    onLive() {
      if (this.onLive) {
        /** now live stream is playing */
        this.$emit("playingLive");
      } else {
        /** now recorded stream is playing */
        this.$emit("playingDvr");
      }
    }
  },
  mounted() {
    if (this.isTimeValid) {
      this.isLoaded = false;
    } else {
      const unwatchValid = this.$watch("isTimeValid", () => {
        this.isLoaded = false;
        unwatchValid();
      });
    }
  },
  methods: {
    startLoadSegment(startTime) {
      if (startTime < this.minTime) {
        startTime = this.minTime;
        console.warn("Start time must be later or equal to stream start");
      }
      const preloadedStartTime = Math.max(
        this.minTime,
        startTime - this.$options.preloadDuration
      );

      this.onLive = false;
      this.segmentStart = preloadedStartTime;
    },
    startLoadLive() {
      this.onLive = true;
      this.segmentStart = this.maxTime;
    },
    handlePlayerPositionChanged(time) {
      if (!this.isLoaded) {
        console.warn("Player position was changed in loading process");
        return;
      }

      const absoluteTime = this.segmentStart + time;
      this.$emit("update:time", absoluteTime);
      if (!this.onLive) {
        this.movePlayer(absoluteTime);
      }
    },
    // @vuese
    // Update flag and duration when player is reloaded
    handleLoaded({ duration }) {
      this.isLoaded = true;
      this.actualSegmentDuration = duration;
    },
    // @vuese
    // Move player without reload
    // @arg time - absolute timestamp of new player position
    movePlayer(time) {
      const newPosition = time - this.segmentStart;

      if (!this.isLoaded) {
        throw Error("Player not movable when it's loading");
      }

      if (this.onLive) {
        throw Error("Player is not movable on live");
      }

      if (newPosition < 0) {
        throw Error(
          `Player position must be non-negative, but it was ${newPosition}`
        );
      }

      if (newPosition > this.actualSegmentDuration) {
        throw Error(
          `Player position must be in player duration range but it was ${newPosition}`
        );
      }

      this.playerPosition = newPosition;
    }
  }
};
</script>
